@@ -1,247 +0,0 @@
<script setup lang="ts">
import ControlValidation from "@/components/control-validation.vue";
import { useControlValidation } from "@/composables/use-control-validation";
import type { ErrorReportMetaDTO } from "@/models/dtos/error-report-meta-dto";
import { HelpMessage } from "@/models/enums/help-message";
import type { CreateErrorReportRequest } from "@/models/requests/create-error-report-request";
import { getCreateErrorReportSchema } from "@/schemas/create-error-report-schema";
import { externalErrorReportsService } from "@/services/external-error-reports-service";
import { notificationsService } from "@/services/notifications-service";
import { computed, onBeforeMount, ref, toRefs } from "vue";
import type { PhoneServiceMetaDTO } from "@/models/dtos/phone-service-meta-dto";
import { contractsService } from "@/services/contracts-service";
import { authService } from "@/services/auth-service";

function getDefaultFormData(contractId: number): CreateErrorReportRequest {
  return {
    contractId,
    type: "",
    address: "",
    description: "",
    area: undefined,
    informer: {
      name: undefined,
      phone: undefined,
    },
  };
}

const props = defineProps<{
  contractId: number;
}>();
const { contractId } = toRefs(props);

const fetchLoading = ref(false);
const loading = ref(false);
const errorReportMeta = ref<ErrorReportMetaDTO | undefined>();

const phoneServiceMeta = ref<PhoneServiceMetaDTO | undefined>();

const formData = ref(getDefaultFormData(contractId.value));

const { result, validate } = useControlValidation(() =>
  getCreateErrorReportSchema(),
);

const pageTitle = computed(
  () => `Skapa felanmälan för ${errorReportMeta.value?.contractName}`,
);

onBeforeMount(async () => {
  fetchLoading.value = true;

  try {
    if (authService.authenticated) {
      phoneServiceMeta.value = await contractsService.getPhoneContract(
        contractId.value,
      );
    }

    errorReportMeta.value =
      await externalErrorReportsService.getErrorReportMeta(contractId.value);
    document.title = pageTitle.value;
  } catch (error) {
    errorReportMeta.value = undefined;
    console.error(error);
  } finally {
    fetchLoading.value = false;
  }
});

async function onCreate() {
  loading.value = true;

  try {
    const valid = validate(formData.value);
    if (!valid) return;

    formData.value.contractId = contractId.value;
    await externalErrorReportsService.createErrorReport(formData.value);
    notificationsService.open({
      text: `Felanmälan skapad för ${errorReportMeta.value?.contractName}.`,
      type: "success",
      timeout: 10_000,
    });
    formData.value = getDefaultFormData(contractId.value);
  } catch (error) {
    notificationsService.open({
      text: "Något gick fel vid skapning av felanmälan.",
      type: "error",
      error,
    });
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <div class="columns is-centered">
    <div class="column is-6">
      <section class="section">
        <template v-if="fetchLoading">
          <p class="title is-4">Laddar...</p>
        </template>
        <template v-else-if="errorReportMeta">
          <template v-if="authService.authenticated">
            <div class="mb-4">
              <img
                class="preview-image mb-2"
                :src="errorReportMeta?.base64ImageData"
                :alt="errorReportMeta?.imageName"
              />
              <p class="title is-4">{{ pageTitle }}</p>
              <p class="is-render-text-as-is">
                {{ phoneServiceMeta?.contractDescription }}
              </p>
            </div>
          </template>
          <template v-else>
            <div class="mb-4">
              <img
                class="preview-image mb-2"
                :src="errorReportMeta?.base64ImageData"
                :alt="errorReportMeta?.imageName"
              />
              <p class="title is-4">{{ pageTitle }}</p>
              <p class="is-render-text-as-is">
                {{ errorReportMeta?.information }}
              </p>
            </div>
          </template>

          <form @submit.prevent="onCreate">
            <div class="field">
              <label class="label" for="type">Typ av fel</label>
              <control-validation
                :result="result"
                field="type"
                #="{ inputClass }"
              >
                <div class="control has-icons-right">
                  <input
                    id="type"
                    v-model="formData.type"
                    class="input"
                    type="text"
                    name="type"
                    :class="inputClass"
                  />
                  <span class="input-hint">{{
                    HelpMessage.ERROR_REPORT_TYPE
                  }}</span>
                </div>
              </control-validation>
            </div>

            <div class="field">
              <label class="label" for="address">Adress/plats</label>
              <control-validation
                :result="result"
                field="address"
                #="{ inputClass }"
              >
                <div class="control has-icons-right">
                  <input
                    id="address"
                    v-model="formData.address"
                    class="input"
                    type="text"
                    name="address"
                    :class="inputClass"
                  />
                  <span class="input-hint">{{
                    HelpMessage.ERROR_REPORT_ADDRESS
                  }}</span>
                </div>
              </control-validation>
            </div>

            <div class="field">
              <label class="label" for="name">Beskrivning</label>
              <control-validation
                :result="result"
                field="description"
                #="{ inputClass }"
              >
                <div class="control has-icons-right">
                  <textarea
                    id="description"
                    v-model="formData.description"
                    class="textarea"
                    name="description"
                    :class="inputClass"
                  />
                  <span class="input-hint">{{
                    HelpMessage.ERROR_REPORT_DESCRIPTION
                  }}</span>
                </div>
              </control-validation>
            </div>

            <div class="field">
              <label class="label" for="name">Område/stolpnummer</label>
              <control-validation
                :result="result"
                field="area"
                #="{ inputClass }"
              >
                <div class="control has-icons-right">
                  <input
                    id="area"
                    v-model="formData.area"
                    class="input"
                    type="text"
                    name="area"
                    :class="inputClass"
                  />
                  <span class="input-hint">{{
                    HelpMessage.ERROR_REPORT_AREA
                  }}</span>
                </div>
              </control-validation>
            </div>

            <div class="field">
              <label class="label" for="informer.name">Namn</label>
              <control-validation
                :result="result"
                field="informer.name"
                #="{ inputClass }"
              >
                <div class="control has-icons-right">
                  <input
                    id="informer.name"
                    v-model="formData.informer!.name"
                    class="input"
                    type="text"
                    name="informer.name"
                    :class="inputClass"
                  />
                  <span class="input-hint">{{
                    HelpMessage.ERROR_REPORT_NAME
                  }}</span>
                </div>
              </control-validation>
            </div>

            <div class="field">
              <label class="label" for="informer.phone">Telefon</label>
              <control-validation
                :result="result"
                field="informer.phone"
                #="{ inputClass }"
              >
                <div class="control has-icons-right">
                  <input
                    id="informer.phone"
                    v-model="formData.informer!.phone"
                    class="input"
                    type="tel"
                    name="informer.phone"
                    :class="inputClass"
                  />
                  <span class="input-hint">{{
                    HelpMessage.ERROR_REPORT_PHONE
                  }}</span>
                </div>
              </control-validation>
            </div>

            <div class="control">
              <button
                class="button is-primary"
                type="submit"
                :class="{ 'is-loading': loading }"
              >
                Skapa
              </button>
            </div>
          </form>
        </template>
        <template v-else>
          <p class="title is-4">Något gick fel.</p>
        </template>
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.preview-image {
  max-height: 10vh;
}
</style>
